import { Button, ContentSectionContainer, EmailAPIFactory, EOperatorTypes, FullBar, GenericWrapperContext, getBrandUrl, Icon, Modal, Search, SubNav, TEmail, TEmailLog, Typography } from "@cakemail-org/ui-components-v2"
import { TSubNavItem } from "@cakemail-org/ui-components-v2/dist/esm/components/subNav/types"
import { Box, SelectChangeEvent, Stack, Tooltip } from "@mui/material"
import classNames from "classnames"
import { useContext, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { DateRangeDD } from "../../components/dateRangeDropdown"
import { getCurrentStoredFilters, setCurrentStoredFilters, updateIframe } from "../../utils/components"
import { returnEmailTagsQuery } from "../../utils/data"
import { getAPIDateRange } from "../../utils/date"
import { EmailReportInformation } from "../emailAPI/components/emailReport/information"
import { EEmailReportSmallPreviewView } from "../emailAPI/components/emailReport/types"
import { EmailReportActivity } from "./containers/activity"
import { EmailReportPerformance } from "./containers/performance"
import "./styles.scss"
import { EEmailReportContainerSubNav, TEmailReportContainer, TEmailReportFilters } from "./types"

export function EmailReportContainer({ tagFilter, onClose }: TEmailReportContainer) {
    const genericWrapperContext = useContext(GenericWrapperContext)
    const frameRef = useRef<HTMLIFrameElement | null>(null)
    const param = useParams()

    const { t, i18n } = useTranslation()

    const filterStorageKey = "emailReportFilters"
    const initFilters: TEmailReportFilters = getCurrentStoredFilters<TEmailReportFilters>({ key: filterStorageKey, accountId: String(genericWrapperContext?.account?.id) })

    const [search, setSearch] = useState<string>(initFilters?.search || "")
    const [openedSearch, setOpenedSearch] = useState(!!initFilters?.search)
    const [timeRange, setTimeRange] = useState<string>(initFilters?.timeRange || "1d")

    const [lastLog, setLastLog] = useState<TEmailLog | null>()
    const [email, setEmail] = useState<TEmail | null>()
    const [loading, setLoading] = useState<boolean>()
    const [error, setError] = useState<boolean>()

    const [preview, setPreview] = useState<any>()
    const [previewView, setPreviewView] = useState<EEmailReportSmallPreviewView>(EEmailReportSmallPreviewView.desktop)

    const [subNav, setSubNav] = useState<EEmailReportContainerSubNav>(EEmailReportContainerSubNav.performance)

    const tagFilterState: any = param.tag || tagFilter || undefined

    useEffect(fetchMetrics, [])
    useEffect(() => {
        const iframe = frameRef.current as HTMLIFrameElement
        updateIframe(iframe, preview)
    }, [preview])

    function setFilterStorage({ ...filters }: Partial<TEmailReportFilters> = {}) {
        setCurrentStoredFilters<TEmailReportFilters>({
            key: filterStorageKey,
            accountId: Number(genericWrapperContext?.account?.id),
            data: {
                timeRange: filters.timeRange || timeRange,
                search: filters.search || search,
            }
        })
    }

    function handleRangeChange(event: SelectChangeEvent<string>) {
        const { name, value } = event.target
        setTimeRange(value)
        setFilterStorage({ timeRange: value })
    }

    function handleSearchChange(cSearch: string) {
        setSearch(cSearch)
        setFilterStorage({ search: cSearch })
    }

    function handleSearchOpen() {
        setSearch("")
        setOpenedSearch(!openedSearch)
    }

    function fetchMetrics() {
        setLoading(true)
        const apiTimeRange = getAPIDateRange({ range: timeRange })
        EmailAPIFactory.listLogs({
            tags: returnEmailTagsQuery(tagFilterState, { operator: EOperatorTypes.and }),
            startTime: apiTimeRange.start,
            endTime: apiTimeRange.end,
            perPage: 1
        }).then((data) => {
            setLastLog(data.data[0] || null)
            if (data.data.length > 0) {
                EmailAPIFactory.getEmail({ id: data.data[0].email_id }).then((data) => {
                    setEmail(data)
                }).catch(() => {
                    setEmail(null)
                })
            }

            setLoading(false)
        }).catch(() => {
            setLastLog(null)
        })
    }

    function handlePreviewClick(event: React.MouseEvent<HTMLButtonElement>) {
        const { name, value } = event.currentTarget
        setPreviewView(name as EEmailReportSmallPreviewView)
        setPreview(email?.content.html)
    }

    function handlePreviewViewClick(event: React.MouseEvent<HTMLButtonElement>) {
        const { name, value } = event.currentTarget
        if (name === previewView) {
            setPreviewView(EEmailReportSmallPreviewView.desktop)
        } else {
            setPreviewView(name as EEmailReportSmallPreviewView)
        }
    }

    function handleSubNavClick(event: React.MouseEvent<HTMLButtonElement>, item: TSubNavItem) {
        setSubNav(item.name as EEmailReportContainerSubNav)
    }


    const previewSelectionClass = classNames("templatePreview", {
        desktop: previewView === EEmailReportSmallPreviewView.desktop,
        mobile: previewView === EEmailReportSmallPreviewView.mobile,
        heatmap: previewView === EEmailReportSmallPreviewView.heatmap,
        hidden: !preview
    })

    let previewActions: any[] = []

    if (preview) {
        previewActions = [
            <Tooltip title={t("email-preview-toggle-mobile-view")}>
                <Box>
                    <Button
                        iconOnly
                        onClick={handlePreviewViewClick}
                        name={EEmailReportSmallPreviewView.mobile}
                        variant="outlined"
                        color={previewView === EEmailReportSmallPreviewView.mobile ? "primary" : "body1"}>
                        <Icon name="Mobile18" />
                    </Button>
                </Box>
            </Tooltip>,
            /*<Tooltip title={t("email-preview-toggle-heatmap-view")}>
                <Box>
                    <Button
                        onClick={handlePreviewViewClick}
                        name={EEmailReportSmallPreviewView.heatmap}
                        variant="outlined"
                        color={previewView === EEmailReportSmallPreviewView.heatmap ? "primary" : "body1"}>
                        <Icon name="Clicks18" />
                    </Button>
                </Box>
            </Tooltip>*/
        ]
    }

    return <Modal
        className="emailReportModal"
        open
        heading={{
            logoUrl: getBrandUrl({
                elKey: "logo",
                brand: genericWrapperContext?.partnerBrand,
                locale: genericWrapperContext?.user?.language
            }),
            titleContent: [<Typography key="emailReportTitle" variant="title">{t(preview ? "general-email-preview" : "general-email-report", { count: 1 })}</Typography>],
            closeBtnOnClick: () => {
                if (preview) {
                    setPreview(undefined)
                } else {
                    onClose()
                }
            },
            closeBtnText: t(preview ? "general-close-preview" : "general-close"),
            actions: previewActions
        }}

        content={<Stack className="emailReportContainer" direction="row" width="100%">
            {!preview && <>
                <Stack className="emailReportSideBar">
                    <EmailReportInformation lastLog={lastLog} />
                    {/*<EmailReportSmallPreview onClick={handlePreviewClick} email={email} />*/}
                </Stack>
                <ContentSectionContainer className="emailReportStatsContainer">
                    <FullBar
                        left={<Stack justifyItems="flex-start">
                            <Typography variant="body2S" className="support uppercase" >{lastLog?.tags?.join(", ") || "-"}</Typography>
                            <SubNav
                                initialSelected={subNav}
                                menuVariant="text"
                                onClick={handleSubNavClick}
                                items={[
                                    {
                                        name: EEmailReportContainerSubNav.performance,
                                        text: t("general-performance")
                                    },
                                    {
                                        name: EEmailReportContainerSubNav.activity,
                                        text: t("general-activity_one")
                                    }
                                ]}
                            />
                        </Stack>}
                        right={[
                            //subNav === EEmailReportContainerSubNav.activity && <Button key="email-api-seart" variant="outlined" color="body1" onClick={handleSearchOpen}><Icon name="Search18" /></Button>,
                            <DateRangeDD
                                key="email-report-heading-timeRange"
                                onChange={handleRangeChange}
                                value={String(timeRange)}
                            />
                        ]}
                    />

                    {openedSearch && subNav === EEmailReportContainerSubNav.activity &&
                        <Search placeholder={t("email-api-search-placeholder")} initValue={search} className="emailsSearch" onChange={handleSearchChange} onClose={handleSearchOpen} useClickAway />
                    }

                    {subNav === EEmailReportContainerSubNav.performance && <EmailReportPerformance timeRange={timeRange} tagFilter={tagFilterState} />}
                    {subNav === EEmailReportContainerSubNav.activity && <EmailReportActivity timeRange={timeRange} tagFilter={tagFilterState} />}

                </ContentSectionContainer>

            </>}

            <Box className={previewSelectionClass}>
                <iframe ref={frameRef}></iframe>
            </Box>


        </Stack>}
    />
}